import boatLocationPayload, { type Place } from 'components/forms/utils/boatLocationPayload';
import { trackRudderstackEvent } from 'hooks/useRudderStackTrack';

/**
 * This is a “click” event that fires when a user clicks on one of the Google Maps suggestions, recording both the
 * text entered by the user that went to google and the Google Maps address payload of what they clicked on.
 */
export const trackSuggestionSelection = ({
  currentUserTextInput,
  place,
}: {
  currentUserTextInput: string;
  place: Place;
}): void => {
  const objectProperties = JSON.stringify(analyticsObjectProperties(currentUserTextInput, place));
  const payload = {
    component_type: 'form_input_field_suggestion_drop_down',
    object: 'boat_address',
    context: 'list_boat_get_started_widget',
    object_properties: objectProperties,
  };

  trackRudderstackEvent('click', payload);
};

/**
 * This event is fired when the user is shown either type of validation error. It will show what the user inputted
 * as well as what the validation error was
 */
export const trackAddressValidationError = ({
  currentUserTextInput,
  place,
  formErrors,
  visibleValidationError,
}: {
  currentUserTextInput: string;
  place: Place;
  // These are internal errors not exposed to the user.
  formErrors: Record<string, any>;
  // This is the user facing error message.
  visibleValidationError: string;
}): void => {
  if (Object.keys(formErrors).length === 0) return;

  // Code beyond this point only runs if there are validation errors.

  // formErrors contains a ref which has circular references and breaks JSON.stringify.
  const normalizedFormErrors = Object.fromEntries(
    Object.entries(formErrors).map(([key, value]) => [key, { message: value.message, type: value.type }])
  );

  const sharedObjectProperties = analyticsObjectProperties(currentUserTextInput, place);
  const objectProperties = JSON.stringify({
    ...sharedObjectProperties,
    validation_error: visibleValidationError,
    form_errors: JSON.stringify(normalizedFormErrors),
  });
  const payload = {
    component_type: 'form_input_field',
    object: 'field_error',
    context: 'list_boat_get_started_widget',
    object_properties: objectProperties,
  };

  trackRudderstackEvent('show', payload);
};

/**
 * This fires when the user ignores googles suggestions and tabs/clicks/enters out without selecting a
 * google address. It would also record the text inputted by the user.
 */
export const trackAddressSuggestionIgnored = ({
  currentUserTextInput,
  currentPlaceWasSelected,
}: {
  currentUserTextInput: string;
  currentPlaceWasSelected: boolean;
}) => {
  const currentUserTextInputEmpty = currentUserTextInput === null || currentUserTextInput.length === 0;
  if (currentPlaceWasSelected || currentUserTextInputEmpty) return;

  // Code beyond this point only runs if the user ignored googles suggestions.

  const objectProperties = JSON.stringify({
    user_text_input: currentUserTextInput,
  });
  const payload = {
    component_type: 'form_input_field',
    object: 'boat_address',
    context: 'list_boat_get_started_widget',
    object_properties: objectProperties,
  };

  trackRudderstackEvent('leave_form_field', payload);
};

/**
 * This is a simple “click” event that fires when a user clicks on the boat_address form field
 * to begin the process.
 */
export const trackAddressFieldClick = () => {
  const payload = {
    component_type: 'form_input_field',
    object: 'boat_address',
    context: 'list_boat_get_started_widget',
    object_properties: JSON.stringify({}),
  };

  trackRudderstackEvent('enter_form_field', payload);
};

const analyticsObjectProperties = (currentUserTextInput: string, place: Place) => ({
  // We update userTextInput on keystroke for the purpose of using in tracking.
  user_text_input: currentUserTextInput ?? '',
  selected_address_object: {
    // This is the raw returned array from google.
    raw_address_components: place?.address_components ?? [],
    // This is the function we use to process the above array into an object that aligns with our schema.
    extracted_address_components: boatLocationPayload(place, {
      lat: place?.geometry?.location?.lat(),
      lng: place?.geometry?.location?.lng(),
    }),
  },
});
